import { useEffect, useRef, useState } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import { EditIcon, PlusIcon } from 'Asset/icon';
import { Condition, Iteration } from 'Component/others';
import { TextBold, TextMedium, TextSemiBold } from 'UiKit/typography';
import ButtonOutline from 'UiKit/button/buttonOutline';
import ButtonSolid from 'UiKit/button/buttonSolid';
import InputSelect from 'UiKit/input-select';
import InputComponent from 'UiKit/input';
import useGetBrand from 'Domain/brand/useGetBrand';
import usePatchUpdateGiveawayMain from 'Domain/brief-live/usePatchUpdateGiveawayMain';

import GiveawayEditableItem from 'View/brief-live/component/giveaway-editable-item';
import { useBriefGiveawayFormState } from 'View/brief-live/state/giveaway-form';

import styles from './styles.module.css';
import { InitialGiveawayItem } from '../const';

const UpdateGiveaway = ({ giveawayData }) => {
  const { t } = useTranslation('desktop-brief-live');
  const [isEditing, setIsEditing] = useState(false);
  const [brandValue, setBrandValue] = useState(null);
  const [titleValue, setTitleValue] = useState('');
  const [localGiveaways, setLocalGiveaways] = useState([]);
  const bottomRef = useRef(null);
  const { updateFilterState } = useBriefGiveawayFormState();

  useEffect(() => {
    updateFilterState({
      brand: {
        ...giveawayData?.brand,
        label: giveawayData?.brand?.name,
      },
    });
    setLocalGiveaways(giveawayData?.giveaways || []);
  }, [giveawayData]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: optionsBrand } = useGetBrand();
  const { mutate: updateGiveawayMain, isPending } = usePatchUpdateGiveawayMain({
    onSuccess: () => setIsEditing(false),
  });

  const handleAddGiveaway = () => {
    setLocalGiveaways((prev) => [...prev, { isNewData: true, ...InitialGiveawayItem }]);
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 0);
  };

  const handleRemoveNewGiveaway = (index) => {
    setLocalGiveaways((prev) => prev.filter((_, i) => i !== index));
  };

  const handleEdit = () => {
    setBrandValue(giveawayData?.brand ? { id: giveawayData.brand.id, value: giveawayData.brand.name, label: giveawayData.brand.name } : null);
    setTitleValue(giveawayData?.title || '');
    setIsEditing(true);
  };

  const handleSave = () => {
    updateGiveawayMain({
      id: giveawayData?.id,
      payload: { brandId: brandValue?.id, title: titleValue },
    });
  };

  return (
    <Box className={styles['form__container']}>
      <Box className={styles['form__box--main']}>
        <Condition
          statement={isEditing}
          fallback={
            <>
              <Box>
                <TextBold className={styles['form__box--giveaway-name']}>{giveawayData?.title}</TextBold>
                <TextMedium className={styles['form__box--giveaway-brand']}>{giveawayData?.brand?.name}</TextMedium>
              </Box>
              <ButtonOutline borderWidth="1.5px" width="32px" height="32px" onClick={handleEdit}>
                <EditIcon color="var(--color-info70)" size="16px" />
              </ButtonOutline>
            </>
          }
        >
          <Box className={styles['form__box--main-edit']}>
            <Flex gap="16px">
              <Box flex={1}>
                <InputSelect
                  label={t('formGiveaway.brand')}
                  placeholder={t('formGiveaway.brand')}
                  options={optionsBrand || []}
                  value={brandValue}
                  onChange={setBrandValue}
                />
              </Box>
              <Box flex={3}>
                <InputComponent
                  label={t('formGiveaway.giveawayName')}
                  placeholder={t('formGiveaway.giveawayName')}
                  value={titleValue}
                  onChange={setTitleValue}
                />
              </Box>
            </Flex>
            <Box className={styles['form__box--main-actions']}>
              <ButtonOutline height="34px" onClick={() => setIsEditing(false)}>
                <TextSemiBold className={styles['form__text--btn-action']}>{t('formGiveaway.cancelButton')}</TextSemiBold>
              </ButtonOutline>
              <ButtonSolid height="34px" isLoading={isPending} onClick={handleSave}>
                <TextSemiBold className={styles['form__text--btn-action-save']}>{t('formGiveaway.saveButton')}</TextSemiBold>
              </ButtonSolid>
            </Box>
          </Box>
        </Condition>
      </Box>

      <Box className={styles['form__box--gift-requirement']}>
        <TextBold className={styles['form__text--gift-requirement-title']}>{t('formGiveaway.prizeAndTerms')}</TextBold>
        <ButtonOutline height={'34px'} leftIcon={<PlusIcon width="14px" height="14px" color="var(--color-info70)" />} onClick={handleAddGiveaway}>
          <TextSemiBold className={styles['form__text--btn-add-gift']}>{t('formGiveaway.prizeAndTerms')}</TextSemiBold>
        </ButtonOutline>
      </Box>

      <Iteration
        items={localGiveaways}
        render={(giveaway, index) => (
          <GiveawayEditableItem
            key={giveaway?.id ?? `new-${index}`}
            t={t}
            giveaway={giveaway}
            giveawayData={giveawayData}
            defaultEditing={giveaway.isNewData}
            onCancel={giveaway?.isNewData ? () => handleRemoveNewGiveaway(index) : undefined}
          />
        )}
      />
      <Box ref={bottomRef} />
    </Box>
  );
};

export default UpdateGiveaway;
